/**
 * What is selected in one grid, and what is being typed into it.
 *
 * `editing` is opened by `edit` and closed by `stopEditing` or `clear`, and by
 * nothing else. Moving the selection does not close it here -- the caller
 * commits first, which closes it, and clearing it here as well would be a second
 * rule saying the same thing that no test could tell from the first.
 *
 *
 * Its own store, and a small one, for a reason the plan is explicit about: the
 * overlay is the only thing that subscribes to it. Upstream recomputes four
 * modifier classes per cell per render to draw the same rectangle, so every cell
 * on screen re-renders whenever the selection moves -- thirty-six hundred of
 * them for a wide table. One absolutely-positioned rectangle over the top means
 * one component re-renders instead.
 *
 * Client-only state with no server representation and no place in the url, so it
 * is Zustand rather than a query, and a factory rather than a singleton, because
 * two grids on screen have two selections.
 */
import { createStore, type StoreApi } from 'zustand/vanilla'

import type { Cell, CellRange } from './selection'
import { singleCell } from './selection'

export interface SelectionState {
  /** The selected region, or null when nothing is selected. */
  range: CellRange | null
  /**
   * The cell being edited, and what has been typed into it so far.
   *
   * Here rather than beside it because being edited is a state of the
   * selection: exactly one cell can be, it is always the one the selection
   * reaches, and moving the selection ends it.
   */
  editing: { cell: Cell; draft: string } | null
}

export interface SelectionActions {
  /** Extends the region from its anchor to here, for shift-click and shift-arrow. */
  extendTo: (cell: Cell) => void
  /**
   * Follows a row that has been renamed.
   *
   * A row created here appears under a temporary id and is renamed when the
   * server answers. A selection still naming the old one points at nothing:
   * the overlay finds no such row and draws nowhere, and the next keystroke
   * edits a row that does not exist.
   */
  renameRow: (from: number, to: number) => void
  replace: (range: CellRange | null) => void
  clear: () => void

  /** Opens the editor on a cell, starting from whatever text is given. */
  edit: (cell: Cell, draft: string) => void
  type: (draft: string) => void
  stopEditing: () => void
}

export type SelectionStore = StoreApi<SelectionState & SelectionActions>

export function createSelectionStore(): SelectionStore {
  return createStore<SelectionState & SelectionActions>((set, get) => ({
    range: null,
    editing: null,

    extendTo: (cell) => {
      const { range } = get()
      // Extending from nothing is selecting: shift-clicking into a grid with no
      // selection has no anchor to reach from, and refusing would leave the
      // click doing nothing at all.
      set({
        range: range === null ? singleCell(cell) : { anchor: range.anchor, head: cell },
      })
    },

    renameRow: (from, to) => {
      const { range, editing } = get()
      const rename = (cell: Cell): Cell =>
        cell.rowId === from ? { ...cell, rowId: to } : cell

      set({
        range:
          range === null
            ? null
            : { anchor: rename(range.anchor), head: rename(range.head) },
        editing: editing === null ? null : { ...editing, cell: rename(editing.cell) },
      })
    },

    replace: (range) => {
      set({ range })
    },

    clear: () => {
      set({ range: null, editing: null })
    },

    edit: (cell, draft) => {
      set({ range: singleCell(cell), editing: { cell, draft } })
    },

    type: (draft) => {
      const { editing } = get()
      if (editing === null) return
      set({ editing: { ...editing, draft } })
    },

    stopEditing: () => {
      set({ editing: null })
    },
  }))
}
